/*
  Ensartet billedstørrelse på produktkort og i kurven.

  Packshots (billeder med hvid baggrund) har meget forskellig hvid kant indbygget i filen,
  så tæppet fylder forskelligt i kassen. Her findes selve produktet i billedet (alt der ikke
  er næsten hvidt), og billedet skaleres og centreres, så produktet fylder den samme plads
  på den grå flade. Miljøbilleder (ikke hvid kant) markeres med .mk-img--photo og vises i
  fuld størrelse uden grå kant (se theme.css).

  I produktgalleriet bruges kun markeringen af miljøbilleder.

  Analysen bruger en lille udgave af billedet (100 px) hentet med CORS fra Shopifys CDN.
*/
(() => {
  const SELECTOR = '.product-card .product-card__image > img, .cart-item__media img, .mk-reco__media img';
  // Produktgalleriet: kun miljøbilleder markeres (fylder kassen); packshots skaleres ikke her
  const GALLERY = '.product__media-item .media-wrapper img, .product__thumbs-container img';
  const SAMPLE = 100;
  const WHITE = 235;
  const cache = new Map();

  const smallSrc = (src) => {
    try {
      const url = new URL(src, location.href);
      url.searchParams.set('width', String(SAMPLE));
      return url.toString();
    } catch (error) {
      return null;
    }
  };

  const analyse = (src) => {
    if (cache.has(src)) return cache.get(src);
    const promise = new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        try {
          const width = SAMPLE;
          const height = Math.max(1, Math.round((SAMPLE * image.naturalHeight) / image.naturalWidth));
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0, width, height);
          const data = context.getImageData(0, 0, width, height).data;
          const isWhite = (x, y) => {
            const i = (y * width + x) * 4;
            return data[i] > WHITE && data[i + 1] > WHITE && data[i + 2] > WHITE;
          };

          let edge = 0;
          let whiteEdge = 0;
          for (let x = 0; x < width; x++) {
            for (const y of [0, height - 1]) {
              edge++;
              if (isWhite(x, y)) whiteEdge++;
            }
          }
          for (let y = 0; y < height; y++) {
            for (const x of [0, width - 1]) {
              edge++;
              if (isWhite(x, y)) whiteEdge++;
            }
          }
          if (whiteEdge / edge < 0.7) return resolve({ photo: true });

          let x0 = width;
          let y0 = height;
          let x1 = -1;
          let y1 = -1;
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              if (!isWhite(x, y)) {
                if (x < x0) x0 = x;
                if (x > x1) x1 = x;
                if (y < y0) y0 = y;
                if (y > y1) y1 = y;
              }
            }
          }
          if (x1 < 0) return resolve(null);
          resolve({
            photo: false,
            ratio: image.naturalWidth / image.naturalHeight,
            box: { x0: x0 / width, y0: y0 / height, x1: (x1 + 1) / width, y1: (y1 + 1) / height },
          });
        } catch (error) {
          resolve(null);
        }
      };
      image.onerror = () => resolve(null);
      image.src = smallSrc(src);
    });
    cache.set(src, promise);
    return promise;
  };

  const apply = (img, result) => {
    if (!result) return;
    if (result.photo) {
      img.classList.add('mk-img--photo');
      img.style.transform = '';
      return;
    }
    if (img.matches(GALLERY)) return;
    const style = getComputedStyle(img);
    const width = img.clientWidth;
    const height = img.clientHeight;
    const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const padY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const innerWidth = width - padX;
    const innerHeight = height - padY;
    if (innerWidth <= 0 || innerHeight <= 0) return;

    // Billedets størrelse med object-fit: contain
    const renderedWidth = Math.min(innerWidth, innerHeight * result.ratio);
    const renderedHeight = renderedWidth / result.ratio;
    const { x0, y0, x1, y1 } = result.box;
    const contentWidth = (x1 - x0) * renderedWidth;
    const contentHeight = (y1 - y0) * renderedHeight;
    const centerX = ((x0 + x1) / 2 - 0.5) * renderedWidth;
    const centerY = ((y0 + y1) / 2 - 0.5) * renderedHeight;
    const k = Math.min(innerWidth / contentWidth, innerHeight / contentHeight);
    if (!isFinite(k) || k <= 1.02) {
      img.style.transform = '';
      return;
    }
    img.style.transformOrigin = '50% 50%';
    img.style.transform = `scale(${k.toFixed(4)}) translate(${(-centerX).toFixed(2)}px, ${(-centerY).toFixed(2)}px)`;
  };

  const process = (img) => {
    const src = img.currentSrc || img.src;
    if (!src || src.startsWith('data:')) return;
    if (img.dataset.mkFit === src && img.clientWidth === Number(img.dataset.mkFitWidth)) return;
    img.dataset.mkFit = src;
    img.dataset.mkFitWidth = String(img.clientWidth);
    analyse(src).then((result) => apply(img, result));
  };

  const visible = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const img = entry.target;
        if (img.complete && img.naturalWidth) process(img);
        else img.addEventListener('load', () => process(img), { once: true });
      });
    },
    { rootMargin: '300px' }
  );

  const resized = new ResizeObserver((entries) => entries.forEach((entry) => process(entry.target)));

  const watch = (root = document) => {
    root.querySelectorAll(`${SELECTOR}, ${GALLERY}`).forEach((img) => {
      if (img.dataset.mkFitWatched) return;
      img.dataset.mkFitWatched = 'true';
      visible.observe(img);
      resized.observe(img);
    });
  };

  watch();
  new MutationObserver(() => watch()).observe(document.body, { childList: true, subtree: true });
})();
